import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useRef } from "react";
import { DevInfoDot } from "#scripts/lib/dev/DevInfoDot.tsx";
import { cn } from "#scripts/lib/utils.ts";
import type { World } from "#scripts/vmc/WorldState.ts";
import { DevEntityDetails } from "#scripts/world-state-demo/DevEntityDetails.tsx";
import { useComponent } from "#scripts/world-state-demo/world-state-context.ts";
import {
  CActions,
  CCollaborators,
  CFeatureFlags,
  CFocus,
  CInlineChart,
  CSuggestions,
  CUndoStack,
  CValidationState,
  CValue,
  CVersionComparison,
} from "./components.ts";

interface LayerProps {
  entityUID: World.UID;
}

function InputLayer({ entityUID }: LayerProps) {
  const value = useComponent(entityUID, CValue);
  const validationState = useComponent(entityUID, CValidationState);
  const versionComparison = useComponent(entityUID, CVersionComparison);
  const focus = useComponent(entityUID, CFocus);

  const currentValue = useAtomValue(value.valueAtom);
  const setValue = useSetAtom(value.valueAtom);
  const isValid = useAtomValue(validationState.isValidAtom);
  const hasChanges = useAtomValue(versionComparison.hasChangesAtom);
  const baseline = useAtomValue(versionComparison.baselineAtom);
  const isFocused = useAtomValue(focus.isFocusedAtom);

  return (
    <div data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:39:10" data-name="div" className="absolute inset-0 flex items-center gap-2 px-4 py-3 font-mono text-xl bg-bg border-2 border-border rounded transition-colors">
      {hasChanges && (
        <div data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:41:14" data-name="div" className="flex items-center gap-2 text-text-tertiary shrink-0 border-r border-border pr-3">
          <span data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:42:17" data-name="span" className="text-xs opacity-60">BEFORE</span>
          <span data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:43:17" data-name="span" className="line-through opacity-50 text-sm">{baseline}</span>
          <span data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:44:17" data-name="span" className="opacity-60">→</span>
        </div>
      )}
      <input
        data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:48:9" data-name="input" className={cn(
          "flex-1 bg-transparent text-text outline-none min-w-0",
          !isValid && "text-destructive",
          isFocused && "ring-2 ring-accent-muted/30 rounded px-1 -mx-1",
        )}
        type="text"
        value={currentValue}
        onChange={(e) => setValue(e.target.value)}
      />
    </div>
  );
}

function ValidationLayer({ entityUID }: LayerProps) {
  const featureFlags = useComponent(entityUID, CFeatureFlags);
  const validationState = useComponent(entityUID, CValidationState);

  const activeFlags = useAtomValue(featureFlags.flagsAtom);
  const validationError = useAtomValue(validationState.errorAtom);

  if (!activeFlags.has("validation") || !validationError) return null;

  return (
    <div data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:71:10" data-name="div" className="absolute -top-9 left-0 text-sm text-destructive bg-destructive/10 px-3 py-1.5 rounded border border-destructive/30 pointer-events-none font-medium">
      ⚠️ {validationError}
    </div>
  );
}

function UndoLayer({ entityUID }: LayerProps) {
  const featureFlags = useComponent(entityUID, CFeatureFlags);
  const undoStack = useComponent(entityUID, CUndoStack);
  const ref = useRef<HTMLDivElement>(null);

  const activeFlags = useAtomValue(featureFlags.flagsAtom);
  const undoHistory = useAtomValue(undoStack.historyAtom);
  const undoIndex = useAtomValue(undoStack.indexAtom);

  useEffect(() => {
    if (ref.current && undoHistory.length > 0) {
      ref.current.animate([{ transform: "scale(1.1)" }, { transform: "scale(1)" }], {
        duration: 200,
        easing: "ease-out",
      });
    }
  }, [undoHistory.length]);

  if (!activeFlags.has("undo") || undoHistory.length === 0) return null;

  return (
    <div
      ref={ref}
      data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:100:7" data-name="div" className="absolute -bottom-9 right-0 text-sm text-text-secondary bg-bg-elevated px-3 py-1.5 rounded border border-border pointer-events-none font-mono"
    >
      ↶ {undoIndex + 1} / {undoHistory.length}
    </div>
  );
}

function CollaboratorCursorsLayer({ entityUID }: LayerProps) {
  const featureFlags = useComponent(entityUID, CFeatureFlags);
  const collaborators = useComponent(entityUID, CCollaborators);

  const activeFlags = useAtomValue(featureFlags.flagsAtom);
  const cursors = useAtomValue(collaborators.cursorsAtom);

  if (!activeFlags.has("presence") || cursors.length === 0) return null;

  return (
    <>
      {cursors.map((cursor, idx) => (
        <div
          key={cursor.userId}
          data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:121:11" data-name="div" className="absolute top-0 bottom-0 w-0.5 pointer-events-none transition-all duration-200"
          style={{
            left: `${cursor.position}%`,
            backgroundColor: cursor.color,
          }}
        >
          <div
            data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:128:13" data-name="div" className="absolute -top-5 left-0 px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap"
            style={{
              backgroundColor: cursor.color,
              color: "#000",
              transform: idx % 2 === 0 ? "translateX(-50%)" : "translateX(-100%)",
            }}
          >
            {cursor.userId}
          </div>
        </div>
      ))}
    </>
  );
}

function KeybindingLayer({ entityUID }: LayerProps) {
  const featureFlags = useComponent(entityUID, CFeatureFlags);
  const actions = useComponent(entityUID, CActions);
  const ref = useRef<HTMLDivElement>(null);

  const activeFlags = useAtomValue(featureFlags.flagsAtom);
  const availableActions = useAtomValue(actions.availableActionsAtom);

  useEffect(() => {
    if (ref.current && availableActions.length > 0) {
      ref.current.animate(
        [
          { opacity: 0, transform: "translateY(-5px)" },
          { opacity: 1, transform: "translateY(0)" },
        ],
        {
          duration: 300,
          easing: "ease-out",
        },
      );
    }
  }, [availableActions.length]);

  if (!activeFlags.has("shortcuts") || availableActions.length === 0) return null;

  const primaryAction = availableActions[0];

  return (
    <div
      ref={ref}
      data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:173:7" data-name="div" className="absolute -top-9 right-0 flex items-center gap-2 px-3 py-1.5 bg-bg-elevated border border-border rounded pointer-events-none"
    >
      <span data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:175:13" data-name="span" className="text-sm text-text-secondary">{primaryAction.label}</span>
      <div data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:176:12" data-name="div" className="flex items-center gap-1">
        {primaryAction.keybinding.split("-").map((key, i) => (
          <kbd key={i} data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:178:24" data-name="kbd" className="px-1.5 py-0.5 text-xs font-mono bg-bg border border-border rounded shadow-sm">
            {key}
          </kbd>
        ))}
      </div>
    </div>
  );
}

function AISuggestionsLayer({ entityUID }: LayerProps) {
  const featureFlags = useComponent(entityUID, CFeatureFlags);
  const suggestions = useComponent(entityUID, CSuggestions);

  const activeFlags = useAtomValue(featureFlags.flagsAtom);
  const aiSuggestions = useAtomValue(suggestions.suggestionsAtom);

  if (!activeFlags.has("ai") || aiSuggestions.length === 0) return null;

  return (
    <div data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:197:10" data-name="div" className="absolute inset-0 bg-accent-muted/5 pointer-events-none flex items-center px-4">
      <span data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:198:13" data-name="span" className="text-sm text-accent-muted/60 italic font-mono">{aiSuggestions[0].text}</span>
    </div>
  );
}

function VizLayer({ entityUID }: LayerProps) {
  const featureFlags = useComponent(entityUID, CFeatureFlags);
  const inlineChart = useComponent(entityUID, CInlineChart);

  const activeFlags = useAtomValue(featureFlags.flagsAtom);
  const dataPoints = useAtomValue(inlineChart.dataPointsAtom);

  if (!activeFlags.has("viz") || dataPoints.length === 0) return null;

  return (
    <div data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:213:10" data-name="div" className="absolute inset-x-0 bottom-0 h-6 flex items-end gap-px pointer-events-none opacity-30 px-2">
      {dataPoints.map((point, idx) => (
        <div key={idx} data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:215:24" data-name="div" className="flex-1 bg-accent-muted rounded-t-sm" style={{ height: `${(point / 50) * 100}%` }} />
      ))}
    </div>
  );
}

interface RectangleDemoProps {
  entityUID: World.UID;
}
/**
 * Rectangle demo with layered UI components showing various editor features
 */
export function RectangleDemo({ entityUID }: RectangleDemoProps) {
  return (
    <div data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:229:10" data-name="div" className="relative p-8 bg-bg-elevated rounded-lg sticky top-4" data-uid={entityUID}>
      <div data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:230:12" data-name="div" className="relative h-16">
        <InputLayer entityUID={entityUID} />
        <ValidationLayer entityUID={entityUID} />
        <UndoLayer entityUID={entityUID} />
        <KeybindingLayer entityUID={entityUID} />
        <CollaboratorCursorsLayer entityUID={entityUID} />
        <AISuggestionsLayer entityUID={entityUID} />
        <VizLayer entityUID={entityUID} />
        <DevInfoDot
          data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:239:11" data-name="DevInfoDot" className="absolute -right-2 -bottom-2"
          dismissKey="rectangle-demo"
          children={<DevEntityDetails data-loc="phosphor-blog/scripts/world-state-demo/scrolly-rectangle-demo/RectangleDemo.tsx:241:39" data-name="DevEntityDetails" uid={entityUID} />}
        />
      </div>
    </div>
  );
}
